import type { ReactNode } from "react";
import {
  Bell,
  Bot,
  Building2,
  Calendar,
  CalendarCheck,
  CheckCheck,
  Eye,
  HandHelping,
  HelpCircle,
  Link2,
  MessageSquare,
  RefreshCw,
  Settings,
  Sparkles,
  User,
  Video,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { HowItWorksPhone } from "./HowItWorksPhone";
import { FloatingFeatureBadge } from "@/components/landing/shared/FloatingFeatureBadge";
import { WA_MOCK, WhatsAppMockInputBar } from "@/components/landing/shared/WhatsAppMockChrome";
import { WhatsAppMockMessageList } from "@/components/landing/shared/WhatsAppMockMessageList";
import { WhatsAppAttachmentBubble } from "@/components/landing/shared/WhatsAppAttachmentBubble";

type StepId = "signup" | "configure" | "sell";

export function HowItWorksStepArt({ step }: { step: StepId }) {
  return (
    <div className="relative flex items-center justify-center">
      <div
        className="pointer-events-none absolute -right-4 top-1/4 h-40 w-40 rounded-full bg-amber-300/50 blur-3xl sm:h-48 sm:w-48"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -left-6 bottom-1/4 h-32 w-32 rounded-full bg-primary/15 blur-3xl"
        aria-hidden
      />

      {step === "signup" && <SignupVisual />}
      {step === "configure" && <ConfigureVisual />}
      {step === "sell" && <SellVisual />}
    </div>
  );
}

function AuthFieldMini({ label, placeholder, type = "text" }: { label: string; placeholder: string; type?: "text" | "password" }) {
  return (
    <div className="space-y-0.5">
      <label className="text-[7px] font-medium text-foreground">{label}</label>
      <div className="relative">
        <div className="flex h-7 items-center rounded-md border border-input bg-background px-2 text-[8px] text-muted-foreground">
          {placeholder}
        </div>
        {type === "password" ? (
          <Eye className="absolute right-1.5 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
        ) : null}
      </div>
    </div>
  );
}

function SignupVisual() {
  return (
    <div className="relative flex items-center justify-center gap-3 sm:gap-5">
      <div
        className={cn(
          "hidden shrink-0 rounded-xl border border-white/70 bg-card px-3 py-2.5 sm:block",
          "shadow-[0_14px_32px_-6px_rgba(15,23,42,0.35)] ring-1 ring-black/[0.06]",
          "animate-cta-float -translate-y-2",
        )}
      >
        <p className="text-[10px] font-bold text-foreground">Cadastro rápido</p>
        <p className="text-[9px] text-muted-foreground">Escolha seu plano</p>
      </div>

      <HowItWorksPhone>
        <div className="flex h-full flex-col overflow-hidden bg-gradient-to-br from-primary/5 via-background to-violet-50/30">
          <div className="h-[36px] shrink-0" aria-hidden />
          <div className="use-case-chat-scroll min-h-0 flex-1 overflow-y-auto px-2 pb-2 pt-1">
            <div className="mb-2 flex justify-center">
              <img src="/logo-light.png?v=6" alt="" className="h-5 w-auto object-contain" aria-hidden />
            </div>

            <div className="rounded-lg border border-border/60 bg-card p-2 shadow-sm">
              <p className="text-center text-[10px] font-bold text-foreground">Bem-vindo</p>
              <p className="mt-0.5 text-center text-[7px] text-muted-foreground">
                Entre ou crie sua conta para começar
              </p>

              <div className="mt-2 grid grid-cols-2 gap-0.5 rounded-md bg-muted p-0.5">
                <div className="rounded-[4px] py-1 text-center text-[7px] font-medium text-muted-foreground">
                  Entrar
                </div>
                <div className="rounded-[4px] bg-background py-1 text-center text-[7px] font-semibold text-foreground shadow-sm">
                  Criar conta
                </div>
              </div>

              <div className="mt-2 space-y-1.5">
                <AuthFieldMini label="Nome completo" placeholder="Seu nome" />
                <AuthFieldMini label="Email" placeholder="seu@email.com" />
                <AuthFieldMini label="Senha" placeholder="••••••••" type="password" />
                <p className="text-[6px] text-muted-foreground">Mínimo 6 caracteres</p>
              </div>

              <div className="mt-1.5 flex items-start gap-1 rounded border border-border/50 bg-muted/30 p-1.5">
                <div className="mt-0.5 h-2.5 w-2.5 shrink-0 rounded-[3px] border border-primary bg-primary" aria-hidden />
                <p className="text-[6px] leading-snug text-muted-foreground">
                  Aceito os Termos de Uso e a Política de Privacidade.
                </p>
              </div>

              <div className="mt-2 w-full rounded-md bg-primary py-1.5 text-center text-[8px] font-semibold text-primary-foreground">
                Criar conta
              </div>

              <div className="relative my-2">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border/60" />
                </div>
                <p className="relative mx-auto w-fit bg-card px-1 text-[6px] uppercase text-muted-foreground">
                  Ou continue com
                </p>
              </div>

              <div className="flex h-7 items-center justify-center gap-1 rounded-md border border-input bg-background text-[7px] font-medium text-foreground">
                <svg className="h-3 w-3" viewBox="0 0 24 24" aria-hidden>
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Entrar com Google
              </div>
            </div>
          </div>
        </div>
      </HowItWorksPhone>
    </div>
  );
}

const configureTonePills = [
  { icon: "🎩", label: "Formal", selected: false },
  { icon: "💼", label: "Profissional", selected: true },
  { icon: "😊", label: "Cordial", selected: false },
  { icon: "🌴", label: "Descontraído", selected: false },
] as const;

function AgentFieldMini({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="space-y-0.5">
      <label className="text-[6px] font-medium text-foreground">{label}</label>
      <div className="flex h-6 items-center rounded-md border border-input bg-background px-1.5 text-[7px] text-foreground">
        {value}
      </div>
      {hint ? <p className="text-[5px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function AgentSectionMini({
  emoji,
  title,
  children,
}: {
  emoji: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-[hsl(var(--agent-border))] bg-[hsl(var(--agent-surface))] p-1.5 shadow-sm">
      <div className="mb-1 flex items-center gap-1 border-b border-[hsl(var(--agent-border))] pb-1">
        <span className="text-[9px] leading-none" aria-hidden>
          {emoji}
        </span>
        <h4 className="text-[7px] font-semibold text-foreground">{title}</h4>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function ConfigureVisual() {
  return (
    <div className="relative flex items-center justify-center gap-2 sm:gap-3 lg:gap-4">
      <div className="hidden shrink-0 flex-col items-end gap-2 sm:flex">
        <FloatingFeatureBadge
          icon={<span className="h-2 w-2 shrink-0 rounded-full bg-[#25D366]" aria-hidden />}
          title="WhatsApp"
          subtitle="Conecte em Integrações"
          borderClassName="border-emerald-200/80"
          floatClassName="animate-cta-float -translate-y-1"
          className="hidden shrink-0 sm:block"
        />
        <FloatingFeatureBadge
          icon={<RefreshCw className="h-3.5 w-3.5 shrink-0 text-violet-600" aria-hidden />}
          title="Follow-up"
          subtitle="1h · até 3 tentativas"
          borderClassName="border-violet-200/80"
          floatClassName="animate-cta-float-delay-1 translate-y-0.5"
          className="hidden shrink-0 sm:block"
        />
        <FloatingFeatureBadge
          icon={<HelpCircle className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--agent-primary))]" aria-hidden />}
          title="Qualificação"
          subtitle="Perguntas antes do atendimento"
          borderClassName="border-green-200/80"
          floatClassName="animate-cta-float-delay-2 translate-y-1"
          className="hidden shrink-0 sm:block"
        />
      </div>

      <HowItWorksPhone>
        <div className="flex h-full flex-col overflow-hidden bg-[hsl(var(--agent-background))]">
          <div className="h-[36px] shrink-0" aria-hidden />

          <div className="shrink-0 border-b border-border/60 bg-background px-2 pb-1.5 pt-0.5">
            <div className="flex items-center gap-1">
              <Sparkles className="h-2.5 w-2.5 shrink-0 text-[hsl(var(--agent-primary))]" aria-hidden />
              <p className="text-[9px] font-bold text-foreground">Configurar Agente</p>
            </div>
            <p className="mt-0.5 text-[6px] text-muted-foreground">Configure com calma — cada detalhe importa.</p>
          </div>

          <div className="shrink-0 bg-background px-2 pb-1 pt-1.5">
            <div className="flex gap-0 rounded-md bg-muted/50 p-0.5">
              {[
                { icon: User, label: "Perfil", active: true },
                { icon: Building2, label: "Negócio", active: false },
                { icon: Settings, label: "Comportamento", active: false },
              ].map(({ icon: Icon, label, active }) => (
                <div
                  key={label}
                  className={cn(
                    "flex min-w-0 flex-1 items-center justify-center gap-0.5 rounded-[3px] py-1 text-[6px]",
                    active ? "bg-background font-semibold text-foreground shadow-sm" : "font-medium text-muted-foreground",
                  )}
                >
                  <Icon className="h-2 w-2 shrink-0" aria-hidden />
                  <span className="truncate">{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="use-case-chat-scroll min-h-0 flex-1 space-y-1.5 overflow-y-auto px-2 py-1.5">
            <AgentSectionMini emoji="👤" title="Identificação">
              <AgentFieldMini label="Nome Interno" value="Agente de Vendas" />
              <AgentFieldMini label="Nome de Apresentação" value="Maria" hint="Nome que o agente usa nas conversas" />
              <div className="space-y-0.5">
                <label className="text-[6px] font-medium text-foreground">Mensagem de Saudação</label>
                <div className="min-h-[28px] rounded-md border border-input bg-background px-1.5 py-1 text-[6px] leading-snug text-foreground">
                  Olá! Sou a Maria, como posso ajudar você hoje? 😊
                </div>
              </div>
            </AgentSectionMini>

            <AgentSectionMini emoji="🎨" title="Personalidade">
              <div className="space-y-0.5">
                <label className="text-[6px] font-medium text-foreground">Função</label>
                <div className="flex h-6 items-center justify-between rounded-md border border-input bg-background px-1.5 text-[7px] text-foreground">
                  <span>💼 Vendas</span>
                  <span className="text-[6px] text-muted-foreground">▾</span>
                </div>
              </div>

              <div className="space-y-0.5">
                <label className="text-[6px] font-medium text-foreground">Tom de Voz</label>
                <div className="flex flex-wrap gap-0.5 pt-0.5">
                  {configureTonePills.map((tone) => (
                    <span
                      key={tone.label}
                      className={cn(
                        "rounded-full border px-1.5 py-0.5 text-[6px] leading-tight",
                        tone.selected
                          ? "border-[hsl(var(--agent-primary))] bg-[hsl(var(--agent-primary))] font-medium text-white"
                          : "border-border bg-muted/50 text-foreground",
                      )}
                    >
                      {tone.icon} {tone.label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="space-y-0.5">
                <label className="text-[6px] font-medium text-foreground">Idioma</label>
                <div className="flex h-6 items-center justify-between rounded-md border border-input bg-background px-1.5 text-[7px] text-foreground">
                  <span>🇧🇷 Português (BR)</span>
                  <span className="text-[6px] text-muted-foreground">▾</span>
                </div>
              </div>
            </AgentSectionMini>

            <div className="rounded-lg border border-[hsl(var(--agent-border))] bg-[hsl(var(--agent-surface))] p-1.5 shadow-sm">
              <div className="mb-1 flex items-center gap-1 border-b border-[hsl(var(--agent-border))] pb-1">
                <span className="text-[9px] leading-none" aria-hidden>
                  🕐
                </span>
                <h4 className="text-[7px] font-semibold text-foreground">Horários de Funcionamento</h4>
              </div>
              <div className="flex items-center justify-between gap-1 rounded-md border border-border/60 bg-muted/30 p-1">
                <div className="min-w-0">
                  <p className="text-[6px] font-medium text-foreground">🔒 Restringir Horário</p>
                  <p className="text-[5px] text-muted-foreground">Não responder fora do horário</p>
                </div>
                <div className="h-3.5 w-6 shrink-0 rounded-full bg-[hsl(var(--agent-primary))] p-0.5">
                  <div className="ml-auto h-2.5 w-2.5 rounded-full bg-white shadow-sm" />
                </div>
              </div>
              <p className="mt-1 text-[6px] text-muted-foreground">Seg–Sex · 9h às 18h · Brasília (GMT-3)</p>
            </div>
          </div>

          <div className="flex shrink-0 gap-1 border-t border-border/60 bg-background px-2 py-1.5">
            <div className="flex-1 rounded-md border border-input py-1 text-center text-[6px] font-medium text-muted-foreground">
              Cancelar
            </div>
            <div className="flex-1 rounded-md bg-[hsl(var(--agent-primary))] py-1 text-center text-[6px] font-semibold text-white">
              Atualizar
            </div>
          </div>
        </div>
      </HowItWorksPhone>

      <div className="hidden shrink-0 flex-col gap-2 sm:flex">
        <FloatingFeatureBadge
          icon={<Bell className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />}
          title="Lembrete"
          subtitle="24h e 2h antes · WhatsApp"
          borderClassName="border-amber-200/80"
          floatClassName="animate-cta-float-delay-1 -translate-y-1"
          className="hidden shrink-0 sm:block"
        />
        <FloatingFeatureBadge
          icon={<Calendar className="h-3.5 w-3.5 shrink-0 text-sky-600" aria-hidden />}
          title="Google Calendar"
          subtitle="Agendamentos automáticos"
          borderClassName="border-sky-200/80"
          floatClassName="animate-cta-float translate-y-0.5"
          className="hidden shrink-0 sm:block"
        />
        <FloatingFeatureBadge
          icon={<HandHelping className="h-3.5 w-3.5 shrink-0 text-orange-500" aria-hidden />}
          title="Escalado para humano"
          subtitle="Regras por funil e estágio"
          borderClassName="border-orange-200/80"
          floatClassName="animate-cta-float-delay-2 translate-y-1"
          className="hidden shrink-0 sm:block"
        />
      </div>
    </div>
  );
}

const sellChatMessages: { side: "customer" | "agent"; text: string; meetLink?: boolean }[] = [
  { side: "customer", text: "Oi! Preciso agendar uma reunião online com o time comercial." },
  { side: "agent", text: "Olá! Claro. É para conhecer a FabChat ou falar de planos?" },
  { side: "customer", text: "Conhecer a plataforma e ver uma demo ao vivo." },
  { side: "agent", text: "Perfeito. Amanhã tenho 14h ou 16h (Brasília). Qual prefere?" },
  { side: "customer", text: "14h funciona. Pode ser pelo Google Meet?" },
  { side: "agent", text: "Sim! Já reservei o horário na agenda." },
  {
    side: "agent",
    text: "Enviei o convite com o link da videochamada:",
    meetLink: true,
  },
  { side: "customer", text: "Perfeito, obrigado! Até amanhã às 14h." },
];

function SellVisual() {
  return (
    <div className="relative flex items-center justify-center gap-2 sm:gap-3 lg:gap-4">
      <div className="hidden shrink-0 flex-col items-end gap-2 sm:flex">
        <FloatingFeatureBadge
          icon={<Zap className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden />}
          title="IA ativa"
          borderClassName="border-amber-200/80"
          floatClassName="animate-cta-float -translate-y-1"
          className="hidden shrink-0 sm:block"
        />
        <FloatingFeatureBadge
          icon={<MessageSquare className="h-3.5 w-3.5 shrink-0 text-violet-600" aria-hidden />}
          title="Mensagem agendada"
          subtitle="Confirmação automática"
          borderClassName="border-violet-200/80"
          floatClassName="animate-cta-float-delay-1 translate-y-0.5"
          className="hidden shrink-0 sm:block"
        />
        <FloatingFeatureBadge
          icon={<Link2 className="h-3.5 w-3.5 shrink-0 text-blue-600" aria-hidden />}
          title="Link enviado"
          subtitle="Meet, produto ou página"
          borderClassName="border-blue-200/80"
          floatClassName="animate-cta-float-delay-2 translate-y-1"
          className="hidden shrink-0 sm:block"
        />
      </div>

      <HowItWorksPhone>
        <div className="flex h-full flex-col" style={{ backgroundColor: WA_MOCK.chatBg }}>
          <header className="shrink-0" style={{ backgroundColor: WA_MOCK.header }}>
            <div className="h-[30px] shrink-0" aria-hidden />
            <div className="flex min-h-[36px] items-center justify-between gap-2 px-2.5 pb-2">
              <div className="min-w-0 flex-1 text-left">
                <p className="truncate text-[8px] font-medium text-white/80">WhatsApp · Vendas</p>
                <p className="truncate text-[11px] font-semibold leading-snug text-white">
                  Agente FabChat
                </p>
              </div>
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/15">
                <Bot className="h-4 w-4 text-white" strokeWidth={1.75} aria-hidden />
              </div>
            </div>
          </header>

          <WhatsAppMockMessageList contentClassName="gap-2">
            {sellChatMessages.map((msg, i) => {
              if (msg.meetLink) {
                return (
                  <div key={i}>
                    <WhatsAppAttachmentBubble
                      side="agent"
                      kind="link"
                      caption={msg.text}
                      fileName="Google Meet"
                      linkUrl="meet.google.com/fabchat-demo"
                    />
                  </div>
                );
              }
              const isAgent = msg.side === "agent";
              return (
                <div
                  key={i}
                  className={cn("flex w-full", isAgent ? "justify-end" : "justify-start")}
                >
                  <div
                    className={cn(
                      "max-w-[90%] rounded-lg px-2.5 py-2 text-[10px] leading-[1.45] shadow-sm",
                      isAgent ? "rounded-tr-none" : "rounded-tl-none",
                    )}
                    style={{
                      backgroundColor: isAgent ? WA_MOCK.outgoing : WA_MOCK.incoming,
                      color: WA_MOCK.text,
                    }}
                  >
                    <p>{msg.text}</p>
                    {isAgent ? (
                      <div className="mt-0.5 flex justify-end">
                        <CheckCheck className="h-3 w-3" style={{ color: WA_MOCK.tick }} aria-hidden />
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </WhatsAppMockMessageList>

          <WhatsAppMockInputBar />
        </div>
      </HowItWorksPhone>

      <div className="hidden shrink-0 flex-col gap-2 sm:flex">
        <FloatingFeatureBadge
          icon={<Video className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />}
          title="Google Meet"
          subtitle="Amanhã, 14h"
          borderClassName="border-primary/25"
          floatClassName="animate-cta-float -translate-y-1"
          className="hidden shrink-0 sm:block"
        />
        <FloatingFeatureBadge
          icon={<CalendarCheck className="h-3.5 w-3.5 shrink-0 text-sky-600" aria-hidden />}
          title="Reunião agendada"
          subtitle="Na agenda do time"
          borderClassName="border-sky-200/80"
          floatClassName="animate-cta-float-delay-1 translate-y-0.5"
          className="hidden shrink-0 sm:block"
        />
        <FloatingFeatureBadge
          icon={<Bell className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />}
          title="Lembrete ativado"
          subtitle="24h e 2h antes"
          borderClassName="border-amber-200/80"
          floatClassName="animate-cta-float-delay-2 translate-y-1"
          className="hidden shrink-0 sm:block"
        />
        <FloatingFeatureBadge
          icon={<RefreshCw className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden />}
          title="Follow-up feito"
          subtitle="Lead qualificado"
          borderClassName="border-emerald-200/80"
          floatClassName="animate-cta-float translate-y-1.5"
          className="hidden shrink-0 sm:block"
        />
      </div>
    </div>
  );
}
